import apiClient from './api'

export interface MenuItem {
  id: number
  name: string
  label: string
  path: string
  icon?: string
  parentId?: number | null
  orderIndex: number
  isActive: boolean
  isVisible: boolean
  component?: string
  description?: string
  roles?: string[] // Nombres de roles que pueden acceder
  children?: MenuItem[]
  createdAt: string
  updatedAt: string
  // Campos adicionales para compatibilidad con la interfaz anterior
  parent_id?: number
  sort_order?: number
  is_active?: boolean
  created_at?: string
  updated_at?: string
  [key: string]: any
}

export interface CreateMenuData {
  name: string
  label?: string
  path: string
  icon?: string
  parent_id?: number | null
  sort_order: number
  is_active: boolean
  roles: string[]
  roleIds?: number[]
}

export interface UpdateMenuData extends Partial<CreateMenuData> {
  id: number
}

class MenuService {
  private baseUrl = 'menus'
  private useTestData = false // Usar backend real; test data solo como fallback

  // Datos de prueba temporales (formato antiguo); usar any para evitar errores de tipos
  private testMenus: any[] = [
    {
      id: 1,
      name: 'Dashboard',
      path: '/',
      icon: 'Home',
      sort_order: 1,
      is_active: true,
      roles: ['admin', 'user'],
      created_at: '2024-01-01T00:00:00Z',
      updated_at: '2024-01-01T00:00:00Z'
    },
    {
      id: 2,
      name: 'Gestión de Sistema',
      path: '',
      icon: 'Settings',
      sort_order: 2,
      is_active: true,
      roles: ['admin'],
      children: [
        {
          id: 3,
          name: 'Usuarios',
          path: '/users',
          icon: 'Users',
          parent_id: 2,
          sort_order: 1,
          is_active: true,
          roles: ['admin'],
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-01T00:00:00Z'
        },
        {
          id: 4,
          name: 'Roles',
          path: '/roles',
          icon: 'Shield',
          parent_id: 2,
          sort_order: 2,
          is_active: true,
          roles: ['admin'],
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-01T00:00:00Z'
        },
        {
          id: 5,
          name: 'Menús',
          path: '/menus',
          icon: 'Menu',
          parent_id: 2,
          sort_order: 3,
          is_active: true,
          roles: ['admin'],
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-01T00:00:00Z'
        }
      ],
      created_at: '2024-01-01T00:00:00Z',
      updated_at: '2024-01-01T00:00:00Z'
    },
    {
      id: 6,
      name: 'CRUDs',
      path: '/cruds',
      icon: 'Database',
      sort_order: 3,
      is_active: true,
      roles: ['admin'],
      created_at: '2024-01-01T00:00:00Z',
      updated_at: '2024-01-01T00:00:00Z'
    },
    {
      id: 7,
      name: 'Asistente IA',
      path: '/ai-assistant',
      icon: 'Bot',
      sort_order: 4,
      is_active: true,
      roles: ['admin', 'user'],
      created_at: '2024-01-01T00:00:00Z',
      updated_at: '2024-01-01T00:00:00Z'
    },
    {
      id: 8,
      name: 'Configuración',
      path: '/settings',
      icon: 'Cog',
      sort_order: 5,
      is_active: true,
      roles: ['admin'],
      created_at: '2024-01-01T00:00:00Z',
      updated_at: '2024-01-01T00:00:00Z'
    }
  ]

  async getMenus(): Promise<MenuItem[]> {
    if (this.useTestData) {
      await new Promise(resolve => setTimeout(resolve, 200))
      return Promise.resolve([...this.testMenus])
    }

    try {
      const response = await apiClient.get(this.baseUrl)
      const menus = response.data?.data || response.data || []

      // Mapear campos camelCase del backend a los esperados por el frontend
      const mapBackendMenu = (menu: any): MenuItem => ({
        ...menu,
        label: menu.label || menu.name,
        parent_id: menu.parentId,
        sort_order: menu.orderIndex,
        is_active: menu.isActive,
        created_at: menu.createdAt,
        updated_at: menu.updatedAt,
        children: menu.children ? menu.children.map(mapBackendMenu) : undefined
      })

      return menus.map(mapBackendMenu)
    } catch (error) {
      // Fallback a datos de prueba si hay error
      return Promise.resolve([...this.testMenus])
    }
  }

  // Obtener menús filtrados por el rol del usuario actual
  async getUserMenus(): Promise<MenuItem[]> {
    if (this.useTestData) {
      await new Promise(resolve => setTimeout(resolve, 200))
      return Promise.resolve([...this.testMenus])
    }

    try {
      const response = await apiClient.get(`${this.baseUrl}/user`)
      
      // El backend devuelve una respuesta con un interceptor de transformación
      const menus = response.data?.data || response.data || []
      
      // Mapear los campos del backend al formato esperado por el frontend
      const mapBackendMenu = (menu: any): MenuItem => ({
        ...menu,
        // Mapear campos del backend (camelCase) a los campos esperados
        label: menu.label || menu.name,
        parent_id: menu.parentId, // Para compatibilidad con código existente
        sort_order: menu.orderIndex,
        is_active: menu.isActive,
        created_at: menu.createdAt,
        updated_at: menu.updatedAt,
        children: menu.children ? menu.children.map(mapBackendMenu) : undefined
      })
      const mapped = menus.map(mapBackendMenu)
      // Cachear por usuario actual
      try {
        const cachePayload = { when: Date.now(), items: mapped }
        localStorage.setItem('cached_user_menus', JSON.stringify(cachePayload))
      } catch {}
      return mapped
    } catch (error) {
      // Fallback preferente: menús cacheados del último login exitoso
      try {
        const raw = localStorage.getItem('cached_user_menus')
        if (raw) {
          const parsed = JSON.parse(raw)
          if (parsed?.items && Array.isArray(parsed.items)) {
            return Promise.resolve(parsed.items)
          }
        }
      } catch {}
      // Último recurso: datos de prueba mínimos
      return Promise.resolve([...this.testMenus])
    }
  }

  async getMenu(id: number): Promise<MenuItem> {
    if (this.useTestData) {
      await new Promise(resolve => setTimeout(resolve, 300))
      const menu = this.findMenuById(id)
      if (!menu) {
        throw new Error('Menú no encontrado')
      }
      return Promise.resolve(menu)
    }

    try {
      const response = await apiClient.get(`${this.baseUrl}/${id}`)
      return response.data.data
    } catch (error) {
      throw new Error('Error al cargar el menú')
    }
  }

  async createMenu(data: CreateMenuData): Promise<MenuItem> {
    if (this.useTestData) {
      await new Promise(resolve => setTimeout(resolve, 500))
      
      const getAllIds = (items: any[]): number[] => {
        let ids: number[] = []
        for (const item of items) {
          ids.push(item.id)
          if (item.children) {
            ids = ids.concat(getAllIds(item.children))
          }
        }
        return ids
      }
      
      const allIds = getAllIds(this.testMenus)
      const maxId = allIds.length > 0 ? Math.max(...allIds) : 0
      
      const newMenu: any = {
        id: maxId + 1,
        ...data,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
      
      if (data.parent_id) {
        const parent = this.findMenuById(data.parent_id)
        if (parent) {
          if (!parent.children) parent.children = []
          parent.children.push(newMenu)
        }
      } else {
        this.testMenus.push(newMenu)
      }
      
      return Promise.resolve(newMenu)
    }

    try {
      // Usar endpoint fijo para evitar pérdida de contexto o rutas vacías
      const endpoint = 'menus'
      // Mapear datos del formulario (snake_case) al DTO del backend (camelCase)
      const payload: any = {
        name: data.name,
        label: data.label ?? data.name, // Enviar label si se provee; fallback al name
        path: data.path,
        icon: data.icon,
        parentId: data.parent_id ?? undefined,
        orderIndex: data.sort_order ?? 0,
        isActive: data.is_active ?? true,
        isVisible: true,
        // component, description y roleIds se omiten si no están disponibles
      }
      if (data.roleIds !== undefined) {
        payload.roleIds = data.roleIds
      }
      const response = await apiClient.post(endpoint, payload)
      return response.data.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al crear el menú')
    }
  }

  async updateMenu(id: number, data: Partial<CreateMenuData>): Promise<MenuItem> {
    if (this.useTestData) {
      await new Promise(resolve => setTimeout(resolve, 500))
      const menu = this.findMenuById(id)
      if (!menu) {
        throw new Error('Menú no encontrado')
      }
      
      Object.assign(menu, {
        ...data,
        updated_at: new Date().toISOString()
      })
      
      return Promise.resolve(menu)
    }

    try {
      // Mapear solo campos presentes hacia el DTO del backend
      const payload: any = {}
      if (data.name !== undefined) payload.name = data.name
      // label es opcional en Update; si algún día el form lo expone, mapearlo
      if (data.label !== undefined) payload.label = data.label
      if (data.path !== undefined) payload.path = data.path
      if (data.icon !== undefined) payload.icon = data.icon
      if (data.parent_id !== undefined) payload.parentId = data.parent_id
      if (data.sort_order !== undefined) payload.orderIndex = data.sort_order
      if (data.is_active !== undefined) payload.isActive = data.is_active
      // isVisible, component, description, roleIds se omiten salvo que el form los provea
      if ((data as any).roleIds !== undefined) payload.roleIds = (data as any).roleIds

      const response = await apiClient.put(`${this.baseUrl}/${id}`, payload)
      return response.data.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al actualizar el menú')
    }
  }

  async deleteMenu(id: number): Promise<void> {
    if (this.useTestData) {
      await new Promise(resolve => setTimeout(resolve, 500))
      this.removeMenuById(id)
      return Promise.resolve()
    }

    try {
      await apiClient.delete(`${this.baseUrl}/${id}`)
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al eliminar el menú')
    }
  }

  async updateMenuOrder(menuId: number, newOrder: number): Promise<void> {
    if (this.useTestData) {
      await new Promise(resolve => setTimeout(resolve, 300))
      const menu = this.findMenuById(menuId)
      if (menu) {
        menu.sort_order = newOrder
        menu.updated_at = new Date().toISOString()
      }
      return Promise.resolve()
    }

    try {
      await apiClient.put(`${this.baseUrl}/${menuId}`, { orderIndex: newOrder })
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al actualizar el orden del menú')
    }
  }

  async toggleMenuStatus(id: number): Promise<MenuItem> {
    if (this.useTestData) {
      await new Promise(resolve => setTimeout(resolve, 300))
      const menu = this.findMenuById(id)
      if (!menu) {
        throw new Error('Menú no encontrado')
      }
      
      menu.is_active = !menu.is_active
      menu.updated_at = new Date().toISOString()
      return Promise.resolve(menu)
    }

    try {
      const response = await apiClient.patch(`${this.baseUrl}/${id}/toggle-status`)
      return response.data.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al cambiar el estado del menú')
    }
  }

  // Obtener menús disponibles como padres (excluyendo el menú actual y sus hijos)
  async getAvailableParents(excludeId?: number): Promise<MenuItem[]> {
    try {
      const menus = await this.getMenus()

      // 1) Construir conjunto de IDs excluidos (menú actual y todos sus descendientes)
      const excluded = new Set<number>()
      if (excludeId) {
        const collectDescendants = (items: MenuItem[]) => {
          for (const it of items) {
            if (it.id === excludeId) {
              // Marcar el nodo y todos sus hijos como excluidos
              const markAll = (node: MenuItem) => {
                excluded.add(node.id)
                if (node.children) node.children.forEach(markAll)
              }
              markAll(it)
            }
            if (it.children?.length) collectDescendants(it.children)
          }
        }
        collectDescendants(menus)
      }

      // 2) Aplanar jerarquía en una lista con nivel de indentación
      type FlatMenu = MenuItem & { level: number }
      const flat: FlatMenu[] = []

      const dfs = (items: MenuItem[], level: number) => {
        for (const item of items) {
          if (!excluded.has(item.id)) {
            flat.push({ ...(item as any), level })
            if (item.children?.length) dfs(item.children, level + 1)
          }
        }
      }
      dfs(menus, 0)

      return flat as any
    } catch (error) {
      throw new Error('Error al cargar menús disponibles como padres')
    }
  }

  // Métodos auxiliares para datos de prueba
  private findMenuById(id: number): MenuItem | null {
    const findInArray = (items: MenuItem[]): MenuItem | null => {
      for (const item of items) {
        if (item.id === id) return item
        if (item.children) {
          const found = findInArray(item.children)
          if (found) return found
        }
      }
      return null
    }
    
    return findInArray(this.testMenus)
  }

  private removeMenuById(id: number): boolean {
    const removeFromArray = (items: MenuItem[]): boolean => {
      for (let i = 0; i < items.length; i++) {
        if (items[i].id === id) {
          items.splice(i, 1)
          return true
        }
        if (items[i].children && removeFromArray(items[i].children!)) {
          return true
        }
      }
      return false
    }
    
    return removeFromArray(this.testMenus)
  }
}

export const menuService = new MenuService()
